import { AccessorialCode, DomainReference } from '@rygen/rygen-ui/model';

import i18n from '@/i18n';

import { ShipmentStopType } from '../shipment/ShipmentStopType';
import { ShipOrder } from '../ShipOrder';

import { ConsolidatedShipmentLineItem } from './ConsolidatedShipmentLineItem';
import { ConsolidatedShipmentStop } from './ConsolidatedShipmentStop';
import { IConsolidatedShipment } from './IConsolidatedShipment';
import { ResequenceState } from './ResequenceState';
import { ValidShipOrder } from './ValidShipOrder';
import { ValidShipOrderShippingPoint } from './ValidShipOrderShippingPoint';

export class ConsolidatedShipment {
  id = undefined;
  owner?: DomainReference;
  stops: ConsolidatedShipmentStop[] = [];

  readonly shipOrders: ValidShipOrder[] = [];

  constructor(opts?: Partial<ConsolidatedShipment>) {
    if (opts?.owner) {
      this.owner = opts.owner;
    }
    if (opts?.shipOrders) {
      this.shipOrders = opts.shipOrders.map((so) => new ValidShipOrder(so));
    }
    if (opts?.stops) {
      this.stops = opts.stops.map((stop) => new ConsolidatedShipmentStop(stop));
    }
  }

  static copy(
    consolidatedShipment: ConsolidatedShipment,
  ): ConsolidatedShipment {
    return new ConsolidatedShipment(consolidatedShipment);
  }

  get isHazmat(): boolean {
    return this.lineItems.some((li) => li.isHazmat);
  }

  get shipOrderIds(): number[] {
    return this.shipOrders.map((so) => so.id);
  }

  get lineItems(): ConsolidatedShipmentLineItem[] {
    const lineItems: ConsolidatedShipmentLineItem[] = [];

    for (const shipOrder of this.shipOrders) {
      const { pickupStopSequenceNumber, dropOffStopSequenceNumber } =
        this.determineShipOrderLineItemStopSequenceNumbers(shipOrder);
      for (const soli of shipOrder.lineItems) {
        if (!soli.id) {
          throw new Error(
            'Invalid State - could not determine shiporder line item id',
          );
        }

        const consolidatedShipmentLineItem = ConsolidatedShipmentLineItem.build(
          {
            shipOrderId: shipOrder.id,
            shipOrderLineItemId: soli.id,
            pickupStopSequenceNumber,
            dropOffStopSequenceNumber,
          },
          soli,
        );

        lineItems.push(consolidatedShipmentLineItem);
      }
    }

    return lineItems;
  }

  get origin(): ConsolidatedShipmentStop | undefined {
    return this.stops.find((x) => x.sequenceNumber === 1);
  }

  get destination(): ConsolidatedShipmentStop | undefined {
    return this.stops.reduce(
      (prev, curr) =>
        (prev?.sequenceNumber || -1) > (curr?.sequenceNumber || -1)
          ? prev
          : curr,
      undefined as ConsolidatedShipmentStop | undefined,
    );
  }

  get isExistingShipment() {
    return this.shipOrders.some((s) => !!s.shipmentId);
  }

  get requestedAccessorials(): AccessorialCode[] {
    return [];
  }

  addShipOrder(shipOrder: ShipOrder): void {
    const validShipOrder = new ValidShipOrder(shipOrder);
    if (this.shipOrderIds.includes(validShipOrder.id)) {
      return;
    }

    this.shipOrders.push(validShipOrder);
    this.addShipOrderShippingPoint(validShipOrder.origin);
    this.addShipOrderShippingPoint(
      validShipOrder.destination,
      validShipOrder.origin,
    );
  }

  protected addShipOrderShippingPoint(
    shipOrderShippingPoint: ValidShipOrderShippingPoint,
    prerequisite?: ValidShipOrderShippingPoint,
  ): void {
    // only consider stops that happen after milestones are fulfilled
    // (i.e. dropoffs can't happen before pickup)
    let stopsAfterPrereq: ConsolidatedShipmentStop[] = this.stops;

    if (prerequisite) {
      const { stop: stopForPrereq } =
        this.findStopByShipOrderShippingPoint(prerequisite);
      if (stopForPrereq) {
        stopsAfterPrereq = stopsAfterPrereq.filter(
          (s) => s.sequenceNumber > stopForPrereq.sequenceNumber,
        );
      }
    }

    stopsAfterPrereq = [...stopsAfterPrereq].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber,
    );

    let indexToInsert = this.stops.length;
    for (const existingStop of stopsAfterPrereq) {
      const canCombine =
        existingStop.location.hasSameAddress(shipOrderShippingPoint.location) &&
        existingStop.requestedAppointmentRange.isOverlappedBy(
          shipOrderShippingPoint.requestedAppointment,
        );

      if (canCombine) {
        existingStop.addShipOrderShippingPoint(shipOrderShippingPoint);
        existingStop.requestedAppointmentRange =
          existingStop.requestedAppointmentRange.intersectionWith(
            shipOrderShippingPoint.requestedAppointment,
          );
        indexToInsert = -1;
        break;
      }

      const shouldGoBeforeExistingStop =
        existingStop.requestedAppointmentRange.isEmpty ||
        existingStop.requestedAppointmentRange.isAfterRange(
          shipOrderShippingPoint.requestedAppointment,
        );
      if (shouldGoBeforeExistingStop) {
        indexToInsert = this.stops.indexOf(existingStop);
        break;
      }
    }

    if (indexToInsert !== -1) {
      // create new stop
      const newStop = new ConsolidatedShipmentStop({
        shipOrderShippingPoints: [shipOrderShippingPoint],
        requestedAppointmentRange: shipOrderShippingPoint.requestedAppointment,
        sequenceNumber: -1,
      });

      // insert at the proper position
      this.stops.splice(indexToInsert, 0, newStop);
      this.updateStopSequence();
    }
  }

  removeShipOrder(shipOrder: ShipOrder): void {
    const localShipOrderIndex = this.shipOrders.findIndex(
      (so) => so.id === shipOrder.id,
    );
    if (localShipOrderIndex === -1) {
      console.warn('Could not find ship order to remove', shipOrder);
      return;
    }

    const [removedShipOrder] = this.shipOrders.splice(localShipOrderIndex, 1);
    this.removeShipOrderShippingPoint(removedShipOrder.origin);
    this.removeShipOrderShippingPoint(removedShipOrder.destination);
  }

  validateStopResequencing(
    oldSequenceNumber: number,
    newSequenceNumber: number,
  ): ResequenceState {
    const stop = this.stops.find((s) => s.sequenceNumber === oldSequenceNumber);
    if (!stop) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.doesNotExist',
        ),
      };
    }

    const prevStop =
      oldSequenceNumber > 1
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber - 1)
        : undefined;
    const nextStop =
      oldSequenceNumber < this.stops.length
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber + 1)
        : undefined;
    const stopType = stop.stopType;
    if (
      (stopType === ShipmentStopType.PICKUP &&
        newSequenceNumber === this.stops.length) ||
      (stopType === ShipmentStopType.DROP_OFF &&
        oldSequenceNumber === this.stops.length &&
        prevStop?.stopType === ShipmentStopType.PICKUP)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.lastStop',
        ),
      };
    } else if (
      (stopType === ShipmentStopType.DROP_OFF && newSequenceNumber === 1) ||
      (stopType === ShipmentStopType.PICKUP &&
        oldSequenceNumber === 1 &&
        nextStop?.stopType === ShipmentStopType.DROP_OFF)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.firstStop',
        ),
      };
    }

    // line items being dropped off at this stop
    const lineItemsBeingDroppedOff = this.lineItems.filter(
      (li) => li.dropOffStopSequenceNumber === oldSequenceNumber,
    );
    // the pickup stops for all the line items that are being dropped off at this stop
    const relatedPickupStops = this.stops.filter((stop) =>
      lineItemsBeingDroppedOff.some(
        (li) => li.pickupStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the highest sequence number of all stops that are pickups for this drop
    // the stop being modified cannot be moved before this sequence number
    const minSeqNum = relatedPickupStops
      .map((stop) => stop.sequenceNumber)
      .reduce((a, b) => Math.max(a, b), -1);

    // line items being picked up at this stop
    const lineItemsBeingPickedUp = this.lineItems.filter(
      (li) => li.pickupStopSequenceNumber === oldSequenceNumber,
    );
    // the drop off stops for all the line items that are being picked up at this stop
    const relatedDropoffStops = this.stops.filter((stop) =>
      lineItemsBeingPickedUp.some(
        (li) => li.dropOffStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the lowest sequence number of all stops that are dropoffs for this pickup
    // the stop being modified cannot be moved after this sequence number
    const maxSeqNum = relatedDropoffStops
      .map((stop) => stop.sequenceNumber)
      .reduce((a, b) => Math.min(a, b), this.stops.length + 1);

    if (newSequenceNumber <= minSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.beforeRelatedPickup',
        ),
      };
    } else if (newSequenceNumber >= maxSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.afterRelatedDropoff',
        ),
      };
    } else {
      return {
        canResequence: true,
        canResequenceReason: '',
      };
    }
  }

  resequenceStop(oldSequenceNumber: number, newSequenceNumber: number): void {
    const { canResequence, canResequenceReason } =
      this.validateStopResequencing(oldSequenceNumber, newSequenceNumber);

    if (!canResequence) {
      throw new Error(canResequenceReason);
    }

    const stopOldIndex = this.stops.findIndex(
      (s) => s.sequenceNumber === oldSequenceNumber,
    );
    if (stopOldIndex === -1) {
      throw new Error(
        `Could not find stop with sequenceNumber ${oldSequenceNumber}`,
      );
    }
    const stopNewIndex = newSequenceNumber - 1;

    const stops = [...this.stops];
    const [stop] = stops.splice(stopOldIndex, 1);
    stops.splice(stopNewIndex, 0, stop);
    for (let i = 0; i < stops.length; i++) {
      stops[i].sequenceNumber = i + 1;
    }
    this.stops.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  }

  protected removeShipOrderShippingPoint(
    shipOrderShippingPoint: ValidShipOrderShippingPoint,
  ): void {
    const { stopIndex, stop } = this.findStopByShipOrderShippingPoint(
      shipOrderShippingPoint,
    );

    if (stop === undefined) {
      throw new Error(
        'IllegalState - could not find shipment stop containing shipOrderShippingPoint with id=' +
          shipOrderShippingPoint.id,
      );
    }

    // remove ship order shipping point from shipment stop
    const shipOrderShippingPointIndex = stop.shipOrderShippingPoints
      .map((x) => x.id)
      .findIndex((x) => x === shipOrderShippingPoint.id);
    if (shipOrderShippingPointIndex === -1) {
      throw new Error(
        'Illegal State - could not find shipOrderShippingPoint with id=' +
          shipOrderShippingPoint.id,
      );
    }
    stop.shipOrderShippingPoints.splice(shipOrderShippingPointIndex, 1);

    // remove stop from shipment if it is empty
    if (stop.shipOrderShippingPoints.length === 0) {
      this.stops.splice(stopIndex, 1);
      this.updateStopSequence();
    }
  }

  protected findStopByShipOrderShippingPoint(
    shipOrderShippingPoint?: ValidShipOrderShippingPoint,
  ): { stopIndex: number; stop?: ConsolidatedShipmentStop } {
    let stopIndex = -1;

    const shipOrderShippingPointId = shipOrderShippingPoint?.id;
    if (shipOrderShippingPointId) {
      stopIndex = this.stops.findIndex((stop) =>
        stop.shipOrderShippingPointIds.includes(shipOrderShippingPointId),
      );
    }

    const stop: ConsolidatedShipmentStop | undefined =
      stopIndex !== -1 ? this.stops[stopIndex] : undefined;

    return { stopIndex, stop };
  }

  protected determineShipOrderLineItemStopSequenceNumbers(
    shipOrder: ValidShipOrder,
  ): {
    pickupStopSequenceNumber: number;
    dropOffStopSequenceNumber: number;
  } {
    let pickupStopSequenceNumber: number | undefined;
    let dropOffStopSequenceNumber: number | undefined;

    for (const shipmentStop of this.stops) {
      const stopIncludesOrigin =
        shipmentStop.shipOrderShippingPointIds.includes(shipOrder.origin.id);
      if (stopIncludesOrigin) {
        pickupStopSequenceNumber = shipmentStop.sequenceNumber;
      }

      const stopIncludesDestination =
        shipmentStop.shipOrderShippingPointIds.includes(
          shipOrder.destination.id,
        );
      if (stopIncludesDestination) {
        dropOffStopSequenceNumber = shipmentStop.sequenceNumber;
      }

      if (pickupStopSequenceNumber && dropOffStopSequenceNumber) {
        break;
      }
    }

    if (!pickupStopSequenceNumber) {
      throw new Error(
        'Invalid State - could not determine line item pickup sequence number',
      );
    }

    if (!dropOffStopSequenceNumber) {
      throw new Error(
        'Invalid State - could not determine line item drop off sequence number',
      );
    }

    return { pickupStopSequenceNumber, dropOffStopSequenceNumber };
  }

  protected updateStopSequence(): void {
    for (let i = 0; i < this.stops.length; i++) {
      this.stops[i].sequenceNumber = i + 1;
    }
  }

  toJSON(): IConsolidatedShipment {
    return {
      shipOrderIds: this.shipOrderIds,
      stops: this.stops,
      lineItems: this.lineItems.map((l) => l.toJSON()),
    };
  }
}
